import {
  type ICouponRepository,
  type ICreateCouponUseCase,
  type IDefinitionService,
  InternalError,
} from '../../../domain/entry.js';

/**
 * Use case for creating a new coupon for a draw.
 *
 * @param couponRepository Coupon repository dependency
 * @param definitionService Definition service dependency
 * @returns Use case executor for creating a coupon
 */
export function createCouponUseCase(
  couponRepository: ICouponRepository,
  definitionService: IDefinitionService
): ICreateCouponUseCase {
  return {
    execute: async ({
      itsProduct,
      couponType,
      drawNumber,
      eventCount,
      betAmount,
    }): ReturnType<ICreateCouponUseCase['execute']> => {
      const definition = await definitionService.resolveDefinitionAsync(itsProduct);

      if (!definition) {
        throw new InternalError(`Unable to load definition for product: ${itsProduct}`);
      }

      if (!definition.isProductIncluded(itsProduct)) {
        throw new InternalError(
          `Unable to create coupon for product: ${itsProduct}, expected one of: ${definition.model.drawProducts.join(', ')}`
        );
      }

      // TODO: [CA]: here we would like to utilize drawService to get the eventCount length instead
      // First try the cache, if no match is found then try fetch, if still not found then throw an error.

      const { couponId, model } = couponRepository.makeCouponModel({
        definition,
        couponType,
        configModel: {
          drawNumber,
          product: itsProduct,
          eventCount,
        },
        betAmount,
      });
      const response = couponRepository.createCoupon(couponId, model);

      if (!response) {
        throw new InternalError(`Unable to create coupon for product ${definition.model.moduleEngine}`);
      }

      return response;
    },
  };
}
